import React, { useCallback, useState } from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import cloneDeep from "lodash/cloneDeep";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ConfirmLeaveDialog, Spinner } from "components/common";
import CurrentUser from "logic/users/CurrentUser";
import useCurrentUser from "hooks/useCurrentUser";
import useEventDefinitionConfigFromLocalStorage from "components/event-definitions/hooks/useEventDefinitionConfigFromLocalStorage";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useScopePermissions from "hooks/useScopePermissions";
import { useEventNotifications } from "components/event-notifications/hooks/useEventNotifications";
import useEventDefinitionSteps, { INITIAL_EVENT_DEFINITION } from "./useEventDefinitionCommonSteps";
import EventDetailsForm from "./EventDetailsForm";
import EventConditionForm from "./EventConditionForm";
import EventDefinitionForm, { getStepKeys } from "./EventDefinitionForm";
import useEventDefinitionMutations from "../hooks/useEventDefinitionMutations";
import {
  updateEventDefinition,
  useGetEntityTypes,
  useGetListEventsClusterConfig,
  EVENT_DEFINITIONS_QUERY_KEY
} from "../hooks/useEventDefinitions";
import { eventDefinitionTypeTitle } from "../pluginTitles";
import { translateValidationResult } from "util/translateApiError";
const STEP_KEYS = {
  EVENT_DETAILS: "event-details",
  CONDITION: "condition",
  SUMMARY: "summary"
};
export const getConditionPlugin = (type) => {
  if (type === void 0) {
    return { displayName: null, hideFieldsStep: false, defaultConfig: {} };
  }
  return PluginStore.exports("eventDefinitionTypes").find((edt) => edt.type === type) || {};
};
const EventDefinitionFormContainer = ({
  action = "edit",
  eventDefinition: eventDefinitionInitial = INITIAL_EVENT_DEFINITION,
  formControls = void 0,
  initialStep = "event-details",
  onCancel = void 0,
  onChangeStep = void 0,
  onEventDefinitionChange = () => {
  },
  onSubmit = void 0
}) => {
  var _a, _b, _c, _d;
  const { t } = useTranslation("event_definitions");
  const [activeStep, setActiveStep] = useState(initialStep);
  const { configFromLocalStorage, hasLocalStorageConfig } = useEventDefinitionConfigFromLocalStorage();
  const [eventDefinition, setEventDefinition] = useState(() => {
    if (!hasLocalStorageConfig) return eventDefinitionInitial;
    const localStorageConditionPlugin = getConditionPlugin(configFromLocalStorage.type);
    const defaultConfig = (localStorageConditionPlugin == null ? void 0 : localStorageConditionPlugin.defaultConfig) || {};
    const cloned = cloneDeep(eventDefinitionInitial);
    return {
      ...cloned,
      config: {
        ...defaultConfig,
        ...cloned.config,
        ...configFromLocalStorage
      }
    };
  });
  const [validation, setValidation] = useState({ errors: {} });
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();
  const { loadingScopePermissions, scopePermissions } = useScopePermissions(eventDefinition);
  const { createEventDefinition } = useEventDefinitionMutations();
  const { entityTypes, loadingEntityTypes } = useGetEntityTypes();
  const { eventsClusterConfig, loadingEventsClusterConfig } = useGetListEventsClusterConfig();
  const { data: eventNotificationsData, isLoading: loadingEventNotifications } = useEventNotifications();
  const notifications = (_a = eventNotificationsData == null ? void 0 : eventNotificationsData.notifications) != null ? _a : [];
  const currentUser = useCurrentUser();
  const sendTelemetry = useSendTelemetry(action === "create" ? "new-event-definition" : "edit-event-definition");
  const isNew = action === "create";
  const eventDefinitionType = getConditionPlugin(eventDefinition.config.type);
  const hideFieldsStep = (_b = eventDefinitionType == null ? void 0 : eventDefinitionType.hideFieldsStep) != null ? _b : false;
  const currentStepKeys = getStepKeys(isNew, hideFieldsStep);
  const isLoading = !entityTypes || loadingEntityTypes || loadingEventNotifications || loadingEventsClusterConfig;
  const defaults = {
    default_backlog_size: eventsClusterConfig == null ? void 0 : eventsClusterConfig.events_notification_default_backlog
  };
  const handleChange = useCallback(
    (key, value) => {
      setEventDefinition((prev) => ({ ...prev, [key]: value }));
      onEventDefinitionChange({ ...eventDefinition, [key]: value });
      setIsDirty(true);
      setValidation((prev) => {
        if (!prev.errors || !(key in prev.errors)) return prev;
        const { [key]: _dropped, ...remaining } = prev.errors;
        return { ...prev, errors: remaining };
      });
    },
    [eventDefinition, onEventDefinitionChange, setEventDefinition, setIsDirty]
  );
  const commonStepProps = {
    key: eventDefinition.id,
    // Recreate components if ID changed
    action,
    entityTypes,
    eventDefinition,
    onChange: handleChange,
    validation,
    currentUser
  };
  const canEdit = scopePermissions.is_mutable;
  const canEditCondition = canEdit || eventDefinition._scope.toUpperCase() === "ILLUMINATE";
  const viewSteps = [
    {
      key: STEP_KEYS.EVENT_DETAILS,
      title: t("details_form.title"),
      telemetryTitle: "Event Details",
      component: /* @__PURE__ */ React.createElement(
        EventDetailsForm,
        {
          ...commonStepProps,
          eventDefinitionEventProcedure: eventDefinition == null ? void 0 : eventDefinition.event_procedure,
          canEdit: scopePermissions.is_mutable
        }
      )
    },
    {
      key: STEP_KEYS.CONDITION,
      title: (_c = eventDefinitionTypeTitle(eventDefinitionType)) != null ? _c : t("steps.condition"),
      telemetryTitle: (_d = eventDefinitionType == null ? void 0 : eventDefinitionType.displayName) != null ? _d : "Condition",
      component: /* @__PURE__ */ React.createElement(EventConditionForm, { ...commonStepProps, canEdit: canEditCondition })
    }
  ];
  const steps = useEventDefinitionSteps({
    viewSteps,
    commonStepProps,
    notifications,
    notificationDefaults: defaults,
    canEdit
  });
  const handleSubmitSuccessResponse = () => {
    setIsDirty(false);
    queryClient.invalidateQueries({ queryKey: EVENT_DEFINITIONS_QUERY_KEY });
    CurrentUser.reload();
    onSubmit();
  };
  const showValidationErrors = (errors) => {
    setValidation(translateValidationResult(errors));
    setActiveStep(currentStepKeys[currentStepKeys.length - 1]);
  };
  const handleSubmitFailureResponse = (errorResponse) => {
    const { body } = errorResponse.additional;
    if (errorResponse.status === 400) {
      if (body && body.failed) {
        showValidationErrors(body);
        return;
      }
      if (body.type && body.type === "ApiError") {
        if (body.message.includes("org.graylog.events.conditions.Expression") || body.message.includes("org.graylog.events.conditions.Expr") || body.message.includes("org.graylog.events.processor.aggregation.AggregationSeries")) {
          showValidationErrors({
            errors: { conditions: [t("form_container.aggregation_condition_invalid")] }
          });
          return;
        }
        if (body.message.includes("embryonic")) {
          showValidationErrors({
            errors: { query_parameters: [t("form_container.query_parameters_undeclared")] }
          });
        }
      }
    }
  };
  const handleSubmit = () => {
    var _a2, _b2;
    setIsDirty(false);
    const tacticsTechniquesTelemetry = {
      tactics_techniques_count: (_b2 = (_a2 = eventDefinition.tactics_techniques) == null ? void 0 : _a2.length) != null ? _b2 : 0
    };
    if (action === "create") {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.CREATE_CLICKED, {
        app_action_value: "create-event-definition-button",
        ...tacticsTechniquesTelemetry
      });
      createEventDefinition(eventDefinition).then(handleSubmitSuccessResponse, handleSubmitFailureResponse);
    } else {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.UPDATE_CLICKED, {
        app_action_value: "update-event-definition-button",
        ...tacticsTechniquesTelemetry
      });
      updateEventDefinition(eventDefinition.id, eventDefinition).then(
        handleSubmitSuccessResponse,
        handleSubmitFailureResponse
      );
    }
  };
  const handleCancel = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.CANCEL_CLICKED, {
      app_action_value: "cancel-button"
    });
    onCancel();
  };
  const changeStep = (step) => {
    onChangeStep == null ? void 0 : onChangeStep(step);
    setActiveStep(step);
  };
  if (isLoading || loadingScopePermissions) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: t("form_container.loading") });
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, isDirty && /* @__PURE__ */ React.createElement(ConfirmLeaveDialog, { question: "Do you really want to abandon this page and lose your changes? This action cannot be undone." }), /* @__PURE__ */ React.createElement(
    EventDefinitionForm,
    {
      steps,
      action,
      activeStep,
      formControls,
      onCancel: handleCancel,
      onChangeStep: changeStep,
      onSubmit: handleSubmit
    }
  ));
};
export default EventDefinitionFormContainer;
